import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/use-api';
import { api, relativeTime } from '../lib/api';
import { Empty, ErrorBanner, Loading, Panel, Status } from '../components/ui';

interface Ticket {
  id: string;
  reference: string;
  raised_by: string;
  subject: string;
  status: string;
  priority: string;
  device_reference: string | null;
  business_group_name: string | null;
  assigned_si_name: string | null;
  opened_at: string;
}

interface Integrator {
  id: string;
  name: string;
}

/** Wireframe C6 — support queue, client-raised vs. SI-raised (FR-C15, FR-C16). */
export function Tickets() {
  const [raisedBy, setRaisedBy] = useState('');
  const [status, setStatus] = useState('');
  const query = new URLSearchParams();
  if (raisedBy) query.set('raisedBy', raisedBy);
  if (status) query.set('status', status);

  const tickets = useApi<{ items: Ticket[] }>(`/internal/tickets?${query}`, [raisedBy, status]);
  const integrators = useApi<{ items: Integrator[] }>('/internal/system-integrators');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function act(path: string, body: unknown) {
    setBusy(true);
    setError(null);
    try {
      await api.post(path, body);
      tickets.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Support tickets</h1>
          <p>Client tickets arrive here directly — never routed through the SI first (FR-C15).</p>
        </div>
      </div>

      <ErrorBanner error={error ?? tickets.error} />

      <div className="filters">
        <div className="field">
          <label htmlFor="raised">Raised by</label>
          <select id="raised" value={raisedBy} onChange={(e) => setRaisedBy(e.target.value)}>
            <option value="">Everyone</option>
            <option value="client">Client</option>
            <option value="system_integrator">System integrator</option>
            <option value="internal">Internal</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="status">Status</label>
          <select id="status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="triaged">Triaged</option>
            <option value="awaiting_si">Awaiting SI</option>
            <option value="awaiting_client">Awaiting client</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      <Panel title={`${tickets.data?.items.length ?? 0} tickets`}>
        {tickets.loading ? (
          <Loading />
        ) : tickets.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Raised by</th>
                <th>Device</th>
                <th>Subject</th>
                <th>Assigned SI</th>
                <th>Status</th>
                <th>Opened</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.data.items.map((ticket) => (
                <tr key={ticket.id}>
                  <td className="mono">
                    {ticket.reference}
                    {ticket.priority === 'urgent' ? <span className="badge danger" style={{ marginLeft: 6 }}>urgent</span> : null}
                  </td>
                  <td>
                    {ticket.raised_by === 'client' ? (
                      <span className="badge info">Client{ticket.business_group_name ? ` — ${ticket.business_group_name}` : ''}</span>
                    ) : (
                      <span className="badge">{ticket.raised_by.replace(/_/g, ' ')}</span>
                    )}
                  </td>
                  <td className="mono">
                    {ticket.device_reference ? (
                      <Link to={`/devices/${ticket.device_reference}`}>{ticket.device_reference}</Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{ticket.subject}</td>
                  <td className="muted">{ticket.assigned_si_name ?? '—'}</td>
                  <td>
                    <Status value={ticket.status} />
                  </td>
                  <td className="muted">{relativeTime(ticket.opened_at)}</td>
                  <td>
                    <div className="actions">
                      <select
                        style={{ width: 150 }}
                        value=""
                        disabled={busy || ['resolved', 'closed'].includes(ticket.status)}
                        onChange={(e) => {
                          if (e.target.value) {
                            act(`/internal/tickets/${ticket.id}/assign-si`, { systemIntegratorId: e.target.value });
                          }
                        }}
                      >
                        <option value="">Dispatch to SI…</option>
                        {(integrators.data?.items ?? []).map((si) => (
                          <option key={si.id} value={si.id}>
                            {si.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="secondary"
                        disabled={busy || ['resolved', 'closed'].includes(ticket.status)}
                        onClick={() => act(`/internal/tickets/${ticket.id}/status`, { status: 'resolved' })}
                      >
                        Resolve
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No tickets match those filters.</Empty>
        )}
      </Panel>
    </>
  );
}
